import {
  Box,
  Input,
  VStack,
  HStack,
  Text,
  IconButton,
  Badge,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X, Sparkles } from 'lucide-react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import { useProperties } from '@/features/properties/hooks/useProperties'
import { FractionInput } from '@/shared/components/FractionInput'
import type { OwnerCreateFormData } from './OwnerCreateModal'

interface NewUnitInlineFormProps {
  index: number
  control: Control<OwnerCreateFormData>
  errors: FieldErrors<OwnerCreateFormData>
  onRemove: () => void
}

export function NewUnitInlineForm({
  index,
  control,
  errors,
  onRemove,
}: NewUnitInlineFormProps) {
  const { t: _t } = useTranslation()
  const { data: properties = [] } = useProperties()

  return (
    <Box
      p={4}
      borderWidth="2px"
      borderRadius="lg"
      borderColor="brand.500"
      bg="brand.50"
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        bg: 'linear-gradient(90deg, var(--chakra-colors-brand-400), var(--chakra-colors-brand-600))',
        borderTopRadius: 'lg',
      }}
    >
      {/* Header avec badge "Nouveau lot" */}
      <HStack justify="space-between" mb={3} pt={2}>
        <HStack gap={2}>
          <Box color="brand.600">
            <Sparkles size={16} />
          </Box>
          <Badge colorPalette="brand" size="sm" variant="solid">
            Nouveau lot #{index + 1}
          </Badge>
        </HStack>
        <IconButton
          variant="ghost"
          size="xs"
          colorPalette="red"
          onClick={onRemove}
          aria-label="Supprimer"
        >
          <X size={14} />
        </IconButton>
      </HStack>

      <VStack gap={3} align="stretch">
        {/* Résidence - Required */}
        <Field.Root invalid={!!((errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.residence_id)} required>
          <Field.Label fontSize="sm" fontWeight="semibold">
            Résidence
          </Field.Label>
          <Controller
            name={`newUnits.${index}.residence_id`}
            control={control}
            render={({ field }) => (
              <NativeSelectRoot position="relative">
                <NativeSelectField {...field} paddingRight="10" bg="white">
                  <option value="">Sélectionner une résidence</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.residence_key
                        ? `${property.residence_key} - ${property.name}`
                        : property.name}
                    </option>
                  ))}
                </NativeSelectField>
                <Box
                  position="absolute"
                  right="3"
                  top="50%"
                  transform="translateY(-50%)"
                  pointerEvents="none"
                  color="gray.500"
                >
                  <ChevronDown size={14} />
                </Box>
              </NativeSelectRoot>
            )}
          />
          {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.residence_id && (
            <Field.ErrorText>
              {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.residence_id?.message}
            </Field.ErrorText>
          )}
        </Field.Root>

        {/* Numéro et Type sur la même ligne */}
        <HStack gap={3} align="start">
          {/* Numéro de lot - Required */}
          <Field.Root invalid={!!((errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.unit_number)} required flex={1}>
            <Field.Label fontSize="sm" fontWeight="semibold">
              Numéro de lot
            </Field.Label>
            <Controller
              name={`newUnits.${index}.unit_number`}
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="A101" size="sm" bg="white" />
              )}
            />
            {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.unit_number && (
              <Field.ErrorText>
                {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.unit_number?.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          {/* Type - Required */}
          <Field.Root invalid={!!((errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.unit_type)} required flex={1}>
            <Field.Label fontSize="sm" fontWeight="semibold">
              Type
            </Field.Label>
            <Controller
              name={`newUnits.${index}.unit_type`}
              control={control}
              render={({ field }) => (
                <NativeSelectRoot position="relative">
                  <NativeSelectField {...field} paddingRight="10" bg="white">
                    <option value="">Type</option>
                    <option value="RESIDENTIAL">Résidentiel</option>
                    <option value="PARKING">Parking</option>
                    <option value="CELLAR">Cave</option>
                    <option value="COMMERCIAL">Commercial</option>
                  </NativeSelectField>
                  <Box
                    position="absolute"
                    right="3"
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                    color="gray.500"
                  >
                    <ChevronDown size={14} />
                  </Box>
                </NativeSelectRoot>
              )}
            />
            {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.unit_type && (
              <Field.ErrorText>
                {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.unit_type?.message}
              </Field.ErrorText>
            )}
          </Field.Root>
        </HStack>

        {/* Étage et Escalier - Optional */}
        <HStack gap={3}>
          <Field.Root flex={1}>
            <Field.Label fontSize="sm" color="gray.600">
              Étage (optionnel)
            </Field.Label>
            <Controller
              name={`newUnits.${index}.floor`}
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="1" size="sm" bg="white" />
              )}
            />
          </Field.Root>

          <Field.Root flex={1}>
            <Field.Label fontSize="sm" color="gray.600">
              Escalier (optionnel)
            </Field.Label>
            <Controller
              name={`newUnits.${index}.stairwell`}
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="A" size="sm" bg="white" />
              )}
            />
          </Field.Root>
        </HStack>

        {/* Date de début et quote-part */}
        <HStack gap={3} align="start">
          <Field.Root invalid={!!((errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.start_date)} required flex={1}>
            <Field.Label fontSize="sm" fontWeight="semibold">
              Date de début
            </Field.Label>
            <Controller
              name={`newUnits.${index}.start_date`}
              control={control}
              render={({ field }) => (
                <Input {...field} type="date" size="sm" bg="white" />
              )}
            />
            {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.start_date && (
              <Field.ErrorText>
                {(errors.newUnits as Array<{ residence_id?: { message?: string }; unit_number?: { message?: string }; unit_type?: { message?: string }; start_date?: { message?: string } }>)?.[index]?.start_date?.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root flex={1}>
            <Field.Label fontSize="sm" fontWeight="semibold" mb={2}>
              Quote-part
            </Field.Label>
            <Controller
              name={`newUnits.${index}.share_numerator`}
              control={control}
              render={({ field: numeratorField }) => (
                <Controller
                  name={`newUnits.${index}.share_denominator`}
                  control={control}
                  render={({ field: denominatorField }) => {
                    const percentage = denominatorField.value
                      ? ((numeratorField.value || 1) / denominatorField.value) * 100
                      : 100

                    return (
                      <FractionInput
                        value={percentage}
                        onChange={(numerator, denominator) => {
                          numeratorField.onChange(numerator)
                          denominatorField.onChange(denominator)
                        }}
                      />
                    )
                  }}
                />
              )}
            />
          </Field.Root>
        </HStack>

        {/* Info message */}
        <Box
          bg="blue.50"
          borderWidth="1px"
          borderColor="blue.200"
          p={2}
          borderRadius="md"
        >
          <Text fontSize="xs" color="blue.800">
            💡 Le lot sera créé automatiquement. Vous pourrez compléter les détails après.
          </Text>
        </Box>
      </VStack>
    </Box>
  )
}
